import React, { ReactElement, useMemo, useState } from 'react';
import { Alert, Button, Col, Row, Spin, Tooltip, Typography } from 'antd';
import { cleanRpcError } from 'client/modules/common/utils/rpcError';
import useHasEditAccess from 'client/modules/users/hooks/useHasEditAccess';
import { MCP_ATTRIBUTES } from '../../constants/mcpAttributes';
import { useGetTeamAttributesQuery } from '../../services/hooks/useGetTeamAttributesQuery';
import { useListTeamAttributeTypesQuery } from '../../services/hooks/useListTeamAttributeTypesQuery';
import EditMcpNumber from './EditMcpNumber';
import McpHardCapSwitch from './McpHardCapSwitch';

type Props = {
  idTeam: number;
};

const MCPSettings = ({ idTeam }: Props): ReactElement => {
  const hasEditAccess = useHasEditAccess();

  const {
    data: attributes,
    isFetching,
    error,
    refetch,
  } = useGetTeamAttributesQuery({
    enabled: Boolean(idTeam),
    params: { id_team: idTeam },
  });

  const { data: attributeTypes } = useListTeamAttributeTypesQuery({
    enabled: Boolean(idTeam),
  });

  const [dismissedError, setDismissedError] = useState<unknown>(null);

  const refetchAttributes = () => {
    refetch();
  };

  const rows = useMemo(
    () =>
      MCP_ATTRIBUTES.map((definition) => {
        const stored = attributes?.find(
          (a) => a.attribute === definition.attribute
        );
        const declared = attributeTypes?.attribute_types?.find(
          (t) => t.attribute === definition.attribute
        );

        return {
          ...definition,
          value: stored?.value ?? null,
          type: declared?.type || stored?.type || definition.fallbackType,
          description: declared?.description || '',
        };
      }),
    [attributes, attributeTypes]
  );

  if (isFetching && !attributes) {
    return (
      <div className="flex justify-center pt-8">
        <Spin size="large" />
      </div>
    );
  }

  const retryButton = (
    <Button size="small" onClick={refetchAttributes} loading={isFetching}>
      Retry
    </Button>
  );

  if (error && !attributes) {
    return (
      <Alert
        className="mt-8"
        type="error"
        showIcon
        message="Failed to load the MCP settings for this team"
        description={cleanRpcError(error)}
        action={retryButton}
      />
    );
  }

  return (
    <Row className="pt-8">
      <Col
        xs={{ span: 24 }}
        md={{ span: 22 }}
        lg={{ span: 16 }}
        xxl={{ span: 12 }}
      >
        {!!error && error !== dismissedError && (
          <Alert
            className="mb-4"
            type="error"
            showIcon
            closable
            onClose={() => setDismissedError(error)}
            message="Failed to refresh the MCP settings for this team"
            description={`${cleanRpcError(
              error
            )} The values below may be out of date.`}
            action={retryButton}
          />
        )}

        {rows.map((row) => (
          <Row justify="space-between" className="mb-3" key={row.attribute}>
            <Col span="12">
              <Typography.Title level={5} className="!font-medium">
                <Tooltip title={row.description || row.attribute}>
                  {`${row.label}:`}
                </Tooltip>
              </Typography.Title>
            </Col>
            <Col span="12" className="flex justify-end items-center">
              {row.kind === 'boolean' ? (
                <div className="mr-3">
                  <McpHardCapSwitch
                    idTeam={idTeam}
                    attribute={row.attribute}
                    label={row.label}
                    type={row.type}
                    value={row.value}
                    refetch={refetchAttributes}
                    disabled={!hasEditAccess}
                  />
                </div>
              ) : (
                <EditMcpNumber
                  idTeam={idTeam}
                  attribute={row.attribute}
                  label={row.label}
                  type={row.type}
                  value={row.value}
                  refetch={refetchAttributes}
                  disabled={!hasEditAccess}
                />
              )}
            </Col>
          </Row>
        ))}
      </Col>
    </Row>
  );
};

export default MCPSettings;
